import { Children } from 'react';
import { HiChevronDown as ChevronDownIcon } from 'react-icons/hi2';
import { Collapsible, Divider } from '@knack/asterisk-react';

interface BuilderAccordionItemProps extends React.ComponentPropsWithoutRef<typeof Collapsible> {
  isDefaultOpen: boolean;
  label: string;
  children: React.ReactNode;
}

function BuilderAccordionItem({
  isDefaultOpen,
  label,
  children,
  ...props
}: BuilderAccordionItemProps) {
  return (
    <Collapsible defaultOpen={isDefaultOpen} {...props}>
      <Collapsible.Trigger className="group flex w-full items-center transition-all">
        <span className="text-sm font-medium text-emphasis">{label}</span>
        <ChevronDownIcon
          size={14}
          className="ml-auto transition-transform duration-300 group-data-[state=open]:rotate-180"
        />
      </Collapsible.Trigger>
      <Collapsible.Content className="mt-4">{children}</Collapsible.Content>
    </Collapsible>
  );
}

function BuilderAccordion({ children }: { children: React.ReactNode }) {
  const numberOfAccordionItems = Children.count(children);

  // If the accordion has more than one item, add a divider between each item
  if (numberOfAccordionItems > 1) {
    return (
      <>
        {Children.map(children, (child, index) => (
          <>
            {child}

            {/* Only add the divider if it's not the last item */}
            {index + 1 !== numberOfAccordionItems && <Divider className="my-4" />}
          </>
        ))}
      </>
    );
  }

  // Otherwise, return the children as is
  return children;
}

const CompoundBuilderAccordion = Object.assign(BuilderAccordion, {
  Item: BuilderAccordionItem
});

export { CompoundBuilderAccordion as BuilderAccordion };
